import { Box, Card, CardContent, Button } from "@mui/material";
import { useState, ChangeEvent, useRef } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import { ApiResponse } from "../../../models/ApiResponse";
import api from "../../../services/ApiService";
import FileDownload from "../../../components/FileDownload";
import Loader from "../../../components/Loader";
import { EXPORT_PRODUCTS_CSV_SEQUENCE } from "../../../utils/constants";
import SessionService from "../../../services/SessionService";
import { Permissions } from "../../../models/Enum";

const ExportProductsFieldSequence = () => {
    const manageAppSetting = SessionService.getInstance().hasPermission(Permissions.ManageAppSettings);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [fileData, setFileData] = useState<any[]>([]);
    const [columnData, setColumnData] = useState<any[]>([]);
    const [isError, setIsError] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [loading, setLoading] = useState(false);

    const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        setSelectedFile(file);
    };

    const handleClear = () => {
        setColumnData([]);
        setFileData([]);
        setIsError(false);
        setSelectedFile(null);
        fileInputRef.current.value = "";
    };
    const fileInputRef = useRef(null);

    const handleBrowseClick = () => {
        setColumnData([]);
        setFileData([]);
        setIsError(false);
        fileInputRef.current.click();
    };

    const processErrorData = (errorResponse) => {
        if (errorResponse?.data?.length > 0) {
            const errors = errorResponse.data.map((item, index) => ({
                ...item,
                id: `${index + 1}`,
            }));
            const columns = Object.keys(errors[0]).map((field) => ({
                field,
                headerName: field,
                width: field === "Sequence" ? 160 : 350,
                disableColumnMenu: true,
                sortable: false,
            }));
            setColumnData(columns);
            setFileData(errors);
            setErrorMessage(errorResponse.message || "An error occurred");
            setSelectedFile(null);
            setIsError(true);
        }
    };

    const handleSubmit = async () => {
        const maxSizeInBytes = 5 * 1024 * 1024; // 5MB
        const formData = new FormData();
        if (selectedFile.size < maxSizeInBytes) {
            formData.append("file", selectedFile); // Assuming 'file' is the key expected by the server
            try {
                const response = await api
                    .put<ApiResponse<any>>("/Picklist/export-product-csv-field-sequence-bulk-update", formData, {
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                    });
                if (response.isSuccess) {
                    toast.success("Field list sequence for Export Products to CSV and with Thumbnail Image to Excel updated successfully");
                    setSelectedFile(null);
                    fileInputRef.current.value = "";
                } else if (!response.isSuccess && response?.data?.length > 0) {
                    toast.warning(response.message);
                    processErrorData(response);
                }
            } catch (error: any) {
                if (error?.response?.data?.data?.length > 0) {
                    const errorResponse = error.response.data;
                    processErrorData(errorResponse);
                }
                console.error("Exception from bulk update field list sequence for Export Products to CSV and with Thumbnail Image to Excel file", error);
            }
        } else {
            toast.error("Upload file less than 5MB");
        }
    };

    const handleExportSequenceFields = async () => {
        let fileName = 'export-products-field-sequence';
        const fileDownload = await FileDownload({
            requestInput: null,
            inputFileName: fileName,
            inputFileFormat: EXPORT_PRODUCTS_CSV_SEQUENCE,
            setLoading
        });
        await fileDownload.handleDownload();
    };

    return (
        <Card className="products-csv-fields-sequence bulk-common-upload">
            <CardContent>
                <>
                    {loading && <Loader isLoading={loading} />}
                    <div className="title">
                        <p>Bulk Update Field list sequence for Export Products to CSV and with Thumbnail Image</p>
                    </div>
                    <Box className="container" >
                        <input
                            className="file-input"
                            type="file"
                            id="fileInput"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            accept=".xls,.xlsx"
                        />
                        <div className="inputWrap">
                            <input
                                type="text"
                                name="fileName"
                                id="fileName"
                                value={selectedFile ? selectedFile.name : ""}
                                readOnly
                            />

                            <p className="file-format-info">
                                XLSX, XLS (max. 5MB).
                                <span className="download-sample"
                                    onClick={handleExportSequenceFields}
                                >
                                    <strong>Export Field List Sequence for Export Products to CSV and With Thumbnail Image</strong>
                                </span>
                            </p>
                        </div>

                        <Button
                            onClick={handleBrowseClick}
                            variant="contained"
                            disabled={!manageAppSetting}
                        >
                            Browse
                        </Button>

                        <Button
                            onClick={handleSubmit}
                            disabled={(!selectedFile || !manageAppSetting) ? true : false}
                        >
                            Submit
                        </Button>
                        <Button className="clear-button"
                            onClick={handleClear}
                            disabled={!manageAppSetting}
                        >
                            Clear
                        </Button>
                    </Box>

                    {isError ? (
                        <>
                            <div className="errors-container">
                                <p>{errorMessage}</p>
                            </div>
                            <Box
                                className="bulk-update-grid-cell products-csv-field-seq-errors-list-grid"
                            >
                                {fileData.length > 0 && (
                                    <DataGrid
                                        rows={fileData}
                                        columns={columnData}
                                        initialState={{
                                            columns: {
                                                columnVisibilityModel: {
                                                    id: false
                                                }
                                            }
                                        }}
                                        slotProps={{
                                            toolbar: {
                                                showQuickFilter: false,
                                            },
                                        }}
                                        className="products-csv-field-seq-error-datagrid"
                                        disableColumnFilter
                                        disableColumnSelector
                                        disableDensitySelector
                                        disableRowSelectionOnClick={true}
                                        getRowHeight={() => "auto"}
                                    />
                                )}
                            </Box>
                        </>
                    ) : (
                        ""
                    )}
                </>
            </CardContent>
        </Card>
    );
};

export default ExportProductsFieldSequence;
